import React, { useEffect, useState } from 'react';
import { adminApi } from '../api/client';
import './css/admin-roadmap.css';

const EMPTY = {
  phase: '', title: '', subtitle: '', description: '', bullets: '',
  buddy_image: '', show_on_homepage: true, status: 'draft', sort_order: 0,
};

export default function AdminRoadmap() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    adminApi.getRoadmap().then(setItems).catch((e) => setError(e.message));
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setForm(EMPTY);
    setEditingId(null);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      phase: item.phase,
      title: item.title,
      subtitle: item.subtitle || '',
      description: item.description || '',
      bullets: (item.bullets || []).join('\n'),
      buddy_image: item.buddy_image || '',
      show_on_homepage: item.show_on_homepage,
      status: item.status,
      sort_order: item.sort_order,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const payload = {
      ...form,
      bullets: form.bullets.split('\n').map((b) => b.trim()).filter(Boolean),
      show_on_homepage: form.show_on_homepage ? 1 : 0,
    };
    try {
      if (editingId) {
        await adminApi.updateRoadmap(editingId, payload);
      } else {
        await adminApi.createRoadmap(payload);
      }
      resetForm();
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this roadmap item?')) return;
    try {
      await adminApi.deleteRoadmap(id);
      if (editingId === id) resetForm();
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="admin-page admin-page--roadmap">
      <header className="admin-page-header">
        <h1>Roadmap Items</h1>
        <p>Manage phases for the homepage preview and full roadmap page.</p>
      </header>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      <div className="admin-two-col">
        <form className="admin-card admin-form" onSubmit={handleSubmit}>
          <h2>{editingId ? 'Edit Phase' : 'New Phase'}</h2>

          <div className="admin-form-row">
            <label className="admin-label">
              Phase #
              <input className="admin-input" value={form.phase} onChange={(e) => setForm({ ...form, phase: e.target.value })} placeholder="01" required />
            </label>
            <label className="admin-label">
              Sort Order
              <input className="admin-input" type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} />
            </label>
          </div>

          <label className="admin-label">
            Title
            <input className="admin-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
          </label>

          <label className="admin-label">
            Subtitle (optional)
            <input className="admin-input" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} placeholder="Now – Pre-Launch" />
          </label>

          <label className="admin-label">
            Short Description (homepage preview)
            <textarea className="admin-textarea" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </label>

          <label className="admin-label">
            Bullet Points (one per line)
            <textarea className="admin-textarea" rows={6} value={form.bullets} onChange={(e) => setForm({ ...form, bullets: e.target.value })} />
          </label>

          <label className="admin-label">
            Buddy Image URL
            <input className="admin-input" value={form.buddy_image} onChange={(e) => setForm({ ...form, buddy_image: e.target.value })} placeholder="/imgs/greenbuddy.png" />
          </label>

          <div className="admin-form-row">
            <label className="admin-label">
              Status
              <select className="admin-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </label>

            <label className="admin-checkbox-label">
              <input type="checkbox" checked={form.show_on_homepage} onChange={(e) => setForm({ ...form, show_on_homepage: e.target.checked })} />
              Show on homepage preview
            </label>
          </div>

          <div className="admin-form-actions">
            <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
              {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
            </button>
            {editingId && (
              <button type="button" className="admin-btn admin-btn-ghost" onClick={resetForm}>Cancel</button>
            )}
          </div>
        </form>

        <div className="admin-list">
          {items.map((item) => (
            <article key={item.id} className="admin-list-card">
              <div className="admin-list-card-header">
                <h3>Phase {item.phase}: {item.title}</h3>
                <span className={`admin-badge admin-badge-${item.status}`}>{item.status}</span>
              </div>
              {item.description && <p className="admin-list-card-body">{item.description}</p>}
              <p className="admin-list-meta">
                Order: {item.sort_order}
                {item.show_on_homepage ? ' · On homepage' : ''}
              </p>
              {item.buddy_image && (
                <img src={item.buddy_image} alt="" className="admin-thumb" />
              )}
              <div className="admin-list-actions">
                <button type="button" className="admin-btn admin-btn-sm" onClick={() => handleEdit(item)}>Edit</button>
                <button type="button" className="admin-btn admin-btn-sm admin-btn-danger" onClick={() => handleDelete(item.id)}>Delete</button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
